// Human review queue for scored roleplay sessions (trainer+ / admin / owner / dev / platform).
import { useCallback, useEffect, useState } from "react";
import {
  fetchArchivedTrainingSessions,
  fetchTrainingReviewQueue,
  submitTrainingHumanReview,
  type ArchivedRoleplaySession,
  type ReviewQueueSession,
  type RoleplayLine,
} from "@/lib/training/trainingApiClient";
import { isRoleplayScore } from "./trainingReviewQueueShared";
import { TrainingReviewArchivedList } from "./TrainingReviewArchivedList";
import { TrainingReviewQueueListItem } from "./TrainingReviewQueueListItem";

type TrainingReviewQueuePanelProps = {
  organizationId: string | null;
};

export function TrainingReviewQueuePanel(props: TrainingReviewQueuePanelProps) {
  const { organizationId } = props;
  const [tab, setTab] = useState<"pending" | "archived">("pending");
  const [queue, setQueue] = useState<ReviewQueueSession[]>([]);
  const [archived, setArchived] = useState<ArchivedRoleplaySession[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [archivedErr, setArchivedErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [mGrounding, setMGrounding] = useState(3);
  const [mTone, setMTone] = useState(3);
  const [mAccuracy, setMAccuracy] = useState(3);
  const [lClarity, setLClarity] = useState(3);
  const [lAnswersCustomer, setLAnswersCustomer] = useState(3);
  const [lFollowUpQuestions, setLFollowUpQuestions] = useState(3);
  const [lOpenEndedQuestions, setLOpenEndedQuestions] = useState(3);
  const [finalizeArchive, setFinalizeArchive] = useState(true);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoadErr(null);
    setLoading(true);
    try {
      const r = await fetchTrainingReviewQueue(organizationId);
      if (!r.ok) {
        setLoadErr(r.error);
        setQueue([]);
        return;
      }
      const data = r.data as { reviewQueue?: ReviewQueueSession[] };
      setQueue(Array.isArray(data.reviewQueue) ? data.reviewQueue : []);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const loadArchived = useCallback(async () => {
    if (!organizationId) return;
    setArchivedErr(null);
    setArchivedLoading(true);
    try {
      const r = await fetchArchivedTrainingSessions(organizationId);
      if (!r.ok) {
        setArchivedErr(r.error);
        setArchived([]);
        return;
      }
      const data = r.data as { archivedSessions?: ArchivedRoleplaySession[] };
      setArchived(Array.isArray(data.archivedSessions) ? data.archivedSessions : []);
    } finally {
      setArchivedLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab === "archived" && organizationId) void loadArchived();
  }, [tab, organizationId, loadArchived]);

  const open = (id: string) => {
    setExpandedId(id);
    setSubmitErr(null);
    setMGrounding(3);
    setMTone(3);
    setMAccuracy(3);
    setLClarity(3);
    setLAnswersCustomer(3);
    setLFollowUpQuestions(3);
    setLOpenEndedQuestions(3);
    setFinalizeArchive(true);
    setNote("");
  };

  const submit = async (sessionId: string, status: "reviewed" | "skipped") => {
    if (!organizationId) return;
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const r = await submitTrainingHumanReview({
        organizationId,
        reviewSessionId: sessionId,
        reviewStatus: status,
        humanModelScores:
          status === "reviewed" ? { grounding: mGrounding, tone: mTone, accuracy: mAccuracy } : undefined,
        humanLearnerScores:
          status === "reviewed"
            ? {
                clarity: lClarity,
                answersCustomer: lAnswersCustomer,
                followUpQuestions: lFollowUpQuestions,
                openEndedQuestions: lOpenEndedQuestions,
              }
            : undefined,
        reviewNote: note.trim() || null,
        finalizeArchive: status === "reviewed" ? finalizeArchive : undefined,
      });
      if (!r.ok) {
        setSubmitErr(r.error);
        return;
      }
      setExpandedId(null);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Select a workspace organization to open the review queue.
      </div>
    );
  }

  return (
    <div className="w-full text-left space-y-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Roleplay reviews</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Score completed roleplay sessions: <strong>AI customer</strong> (grounding, tone, accuracy) and the{" "}
          <strong>trainee</strong> (clarity, answering the customer, follow-ups, open-ended questions). 1 = weak, 5 = strong.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-xs">
            <button
              type="button"
              className={`px-3 py-1.5 font-semibold ${tab === "pending" ? "bg-indigo-600 text-white" : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"}`}
              onClick={() => setTab("pending")}
            >
              Awaiting review
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 font-semibold ${tab === "archived" ? "bg-indigo-600 text-white" : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"}`}
              onClick={() => setTab("archived")}
            >
              Archived
            </button>
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
            onClick={() => void (tab === "pending" ? load() : loadArchived())}
            disabled={tab === "pending" ? loading : archivedLoading}
          >
            {tab === "pending" ? (loading ? "Refreshing…" : "Refresh queue") : archivedLoading ? "Refreshing…" : "Refresh archived"}
          </button>
        </div>
      </div>

      {tab === "archived" ? (
        <TrainingReviewArchivedList
          sessions={archived}
          loading={archivedLoading}
          err={archivedErr}
          onRefresh={loadArchived}
        />
      ) : null}

      {tab === "pending" && loadErr ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{loadErr}</div>
      ) : null}

      {tab === "pending" && !loading && queue.length === 0 ? (
        <div className="text-sm text-gray-600 border border-gray-200 rounded-xl p-4 bg-gray-50 dark:bg-gray-900/40 dark:border-gray-700 dark:text-gray-300">
          No sessions awaiting review. New scored roleplays appear here automatically.
        </div>
      ) : null}

      {tab === "pending" ? (
        <ul className="space-y-3">
          {queue.map((s) => {
            const openExpanded = expandedId === s.id;
            const ai = isRoleplayScore(s.score) ? s.score : null;
            const lines = Array.isArray(s.messages) ? (s.messages as RoleplayLine[]) : [];
            return (
              <TrainingReviewQueueListItem
                key={s.id}
                session={s}
                expanded={openExpanded}
                onToggle={() => (openExpanded ? setExpandedId(null) : open(s.id))}
                ai={ai}
                lines={lines}
                submitErr={submitErr}
                rubric={{
                  mGrounding,
                  setMGrounding,
                  mTone,
                  setMTone,
                  mAccuracy,
                  setMAccuracy,
                  lClarity,
                  setLClarity,
                  lAnswersCustomer,
                  setLAnswersCustomer,
                  lFollowUpQuestions,
                  setLFollowUpQuestions,
                  lOpenEndedQuestions,
                  setLOpenEndedQuestions,
                }}
                note={note}
                setNote={setNote}
                finalizeArchive={finalizeArchive}
                setFinalizeArchive={setFinalizeArchive}
                submitting={submitting}
                onSubmitReview={() => void submit(s.id, "reviewed")}
                onSkip={() => void submit(s.id, "skipped")}
              />
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
